import React from 'react';
import styled from 'styled-components';
import { ButtonGroupProps } from './buttonGroup.types';
import Button from '../Button/button';
import { ButtonGroupWrapper } from './buttonGroup.style';
import Handler from '../../commonTypes/handler.types';
import bindHandlers from '../../utils/handlerHelper';
import { ButtonShapes, ButtonStyles } from '../Button/button.types';
import useControlledState from '../hooks/useControlledState/useControlledState';

const { PILL } = ButtonShapes;
const { DEFAULT, LINK_BLUE } = ButtonStyles;
const DEFAULT_DATA_ATTR = { 'data-testid': 'button-group' };
export const ButtonWrapper = styled.h2``;

const ButtonGroup = ({
  data,
  ariaLabel = '',
  buttonGroupID,
  buttonWrapperProps,
  dataAttr = {},
  defaultSelected = 0,
  handlers = {},
  inline = true,
  multiple,
  overflow,
  selected,
  selectedStyleName = LINK_BLUE,
  ...otherProps
}: ButtonGroupProps) => {
  // give user ability to make this fully controlled or uncontrolled based on requirements
  const [selectedState, setSelectedState] = useControlledState({
    controlled: selected !== undefined ? (Array.isArray(selected) ? selected : [selected]) : undefined,
    default: Array.isArray(defaultSelected) ? defaultSelected : [defaultSelected],
    name: 'ButtonGroup',
  }) as [number[], (state: number[]) => void];

  const { onClick, ...otherHandlers } = handlers;

  const changeHandler: Handler = {
    onClick: (e: Event) => {
      const target = e && (e.currentTarget as HTMLButtonElement);
      const selectedIndex = target && target.getAttribute('data-index');
      let event;
      if (selectedIndex) {
        let selectedArr: number[];
        // coerce to a number since it will be a string off the html element
        const numericalSelectedIndex = +selectedIndex;
        if (multiple) {
          const isSelected = selectedState.indexOf(numericalSelectedIndex);
          // not selected needs to be added
          if (isSelected === -1) {
            selectedArr = [...selectedState, numericalSelectedIndex];
            event = 'select-option';
          } else {
            // is selected already needs to be removed from selected arr
            selectedArr = [...selectedState];
            selectedArr.splice(isSelected, 1);
            event = 'deselect-option';
          }
        } else {
          selectedArr = [numericalSelectedIndex];
          event = 'select-option';
        }
        setSelectedState(selectedArr);

        // if there is a custom onClick passed in fire it here
        // using same return syntax as dropdown, but adding nativeEvent + elementId too
        // event prop seems arbitrary, but doesn't hurt to pass along
        if (onClick) {
          onClick({
            selected: selectedArr,
            changes: data[selectedIndex as unknown as number],
            elementId: buttonGroupID,
            event,
            nativeEvent: e,
          });
        }
      }
    },
  };

  const combinedDataAttr = { ...DEFAULT_DATA_ATTR, ...dataAttr };
  const combinedHandlers: Handler = bindHandlers(null, otherHandlers, changeHandler);

  const buttonGroupButtons = data.map((button, i) => {
    const { dataAttr, ...restButtonProps } = button;
    const selected = selectedState.indexOf(i) !== -1;
    const ariaLabelText = ariaLabel || button.label || '';
    const defaultDataAttr = {
      'data-index': i,
      'data-selected': selected,
      'data-testid': `button--${i}`,
    };
    const combinedDataAttr = { ...defaultDataAttr, ...dataAttr };

    const ButtonComponent = () => (
      <Button
        handlers={combinedHandlers}
        dataAttr={combinedDataAttr}
        ariaLabel={`${ariaLabelText}${selected ? `${ariaLabelText ? ' ' : ''}selected` : ''}`}
        ariaPressed={selected}
        shape={PILL}
        styleName={selected ? selectedStyleName : DEFAULT}
        {...restButtonProps}
      />
    );
    return buttonWrapperProps ? (
      <ButtonWrapper key={`${button.label}-${i}`} {...buttonWrapperProps}>
        <ButtonComponent />
      </ButtonWrapper>
    ) : (
      <ButtonComponent key={`${button.label}-${i}`} />
    );
  });

  return (
    <ButtonGroupWrapper inline={inline} overflow={overflow} {...combinedDataAttr} {...otherProps}>
      {buttonGroupButtons}
    </ButtonGroupWrapper>
  );
};

export default ButtonGroup;
